import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Award, Users, Heart, Star, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useSEO } from '../hooks/useSEO';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const features = [
  { icon: Sparkles, title: 'Luxury Setups', desc: 'Elegantly designed beach dining experiences tailored to perfection' },
  { icon: Award, title: 'Gourmet Cuisine', desc: 'Authentic Jamaican flavors prepared by expert chefs' },
  { icon: Users, title: 'Personalized Service', desc: 'Dedicated team ensuring every detail exceeds expectations' },
  { icon: Heart, title: 'Memorable Moments', desc: 'Creating experiences that last a lifetime' },
];

const TestimonialForm = ({ api }) => {
  const [form, setForm] = useState({ name: '', event: '', rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      setError('Please fill in your name and review.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${api}/testimonials`, {
        ...form,
        date: new Date().toISOString().split('T')[0],
      });
      setSubmitted(true);
      setForm({ name: '', event: '', rating: 5, comment: '' });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto text-center bg-white p-10 rounded-2xl shadow-sm" data-testid="testimonial-success">
        <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
        <h3 className="text-2xl font-bold text-[#1a1918] mb-2">Thank You!</h3>
        <p className="text-[#736c64]">Your review has been submitted and will appear on our site after approval.</p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 px-6 py-2 bg-[#61525a] text-white rounded-full text-sm font-medium transition-all hover:bg-[#5f9dff]"
          data-testid="submit-another-review-btn"
        >
          Submit Another Review
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto bg-white p-8 md:p-10 rounded-2xl shadow-sm" data-testid="testimonial-form">
      <h3 className="text-2xl font-bold text-[#1a1918] mb-2 text-center">Share Your Experience</h3>
      <p className="text-[#736c64] text-center mb-8">We'd love to hear about your Tropiluxe moment</p>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Your Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61525a]/30 focus:border-[#61525a] transition-all text-[#1a1918]"
            data-testid="testimonial-name-input"
          />
          <input
            type="text"
            placeholder="Event Type (e.g. Anniversary)"
            value={form.event}
            onChange={(e) => setForm({ ...form, event: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61525a]/30 focus:border-[#61525a] transition-all text-[#1a1918]"
            data-testid="testimonial-event-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#736c64] mb-2">Your Rating</label>
          <div className="flex gap-1" data-testid="testimonial-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setForm({ ...form, rating: star })}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`rating-star-${star}`}
              >
                <Star
                  size={28}
                  className={star <= form.rating ? 'text-[#fad24b] fill-[#fad24b]' : 'text-gray-300'}
                />
              </button>
            ))}
          </div>
        </div>
        <textarea
          placeholder="Tell us about your experience..."
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={4}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61525a]/30 focus:border-[#61525a] transition-all resize-none text-[#1a1918]"
          data-testid="testimonial-comment-input"
        />
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-[#61525a] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all hover:bg-[#5f9dff] disabled:opacity-50"
          data-testid="testimonial-submit-btn"
        >
          <Send size={18} />
          {submitting ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>
    </div>
  );
};

const Home = () => {
  useSEO('/');
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/testimonials`)
      ]);
      setPackages(packagesRes.data.packages || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1918] text-white text-xl">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.pexels.com/photos/5864498/pexels-photo-5864498.jpeg")' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" data-testid="hero-title">
            Experience the Elegance of
            <span className="block mt-2 text-[#fad24b]">Luxury Dining</span>
            <span className="block mt-2 text-white">on Jamaica's Beaches</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed">
            Create unforgettable moments with our exclusive beach dinner experiences
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/packages"
              className="px-8 py-4 bg-[#61525a] text-white rounded-full font-semibold text-lg no-underline transition-all duration-300 hover:bg-[#5f9dff] hover:shadow-2xl hover:scale-105"
              data-testid="hero-explore-btn"
            >
              Explore Packages
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-[#61525a] rounded-full font-semibold text-lg no-underline transition-all duration-300 hover:bg-[#f7f5f2] hover:shadow-2xl hover:scale-105"
              data-testid="hero-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-[#f7f5f2]" data-testid="features-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">Why Choose Tropiluxe</h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              We transform ordinary moments into extraordinary memories
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-white p-8 rounded-2xl shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#61525a] to-[#5f9dff] rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110">
                  <f.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1a1918] mb-3">{f.title}</h3>
                <p className="text-[#736c64] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Packages */}
      <section className="py-20 bg-white" data-testid="packages-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">Our Signature Packages</h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              Choose from our carefully curated experiences or let us create something unique for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                data-testid={`home-package-${pkg.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    alt={pkg.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    src={getImageUrl(pkg.image) || 'https://images.pexels.com/photos/5864498/pexels-photo-5864498.jpeg'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-white/90 text-sm mb-4 line-clamp-2">{pkg.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold">${pkg.price}</span>
                    <Link
                      to={`/packages#pkg-${pkg.id}`}
                      className="px-6 py-2 bg-white text-[#61525a] rounded-full font-medium text-sm no-underline transition-all duration-300 hover:bg-[#5f9dff] hover:text-white"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#f7f5f2]" data-testid="testimonials-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">What Our Guests Say</h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              Don't just take our word for it - hear from those who've experienced the magic
            </p>
          </div>

          {testimonials.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {testimonials.slice(0, 3).map((t) => (
              <div
                key={t.id}
                className="bg-white p-8 rounded-2xl shadow-sm transition-all duration-300 hover:shadow-xl"
                data-testid={`testimonial-${t.id}`}
              >
                <div className="flex mb-4">
                  {Array.from({ length: t.rating }, (_, i) => (
                    <span key={i} className="text-[#fad24b] text-xl">&#9733;</span>
                  ))}
                </div>
                <p className="text-[#736c64] mb-6 leading-relaxed italic">"{t.comment}"</p>
                <div>
                  <p className="font-bold text-[#1a1918]">{t.name}</p>
                  <p className="text-sm text-[#736c64]">{t.event}</p>
                </div>
              </div>
            ))}
          </div>
          )}

          <TestimonialForm api={API} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#61525a] to-[#5f9dff] text-white" data-testid="cta-section">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Create Your Perfect Evening?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
            Let us bring your vision to life with an unforgettable luxury beach dining experience
          </p>
          <Link
            to="/packages"
            className="px-10 py-4 bg-white text-[#61525a] rounded-full font-bold text-lg no-underline transition-all duration-300 hover:bg-[#fad24b] hover:shadow-2xl hover:scale-105 inline-block"
            data-testid="cta-book-btn"
          >
            Book Your Experience
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
